import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useInView } from '../../hooks/useInView';

const AboutPage = () => {
  const [ref1, isInView1] = useInView({ threshold: 0.2 });
  const [ref2, isInView2] = useInView({ threshold: 0.2 });
  const [ref3, isInView3] = useInView({ threshold: 0.2 });
  const [ref4, isInView4] = useInView({ threshold: 0.2 });
  const [ref5, isInView5] = useInView({ threshold: 0.2 });

  const values = [
    {
      icon: 'ri-shield-check-line',
      title: 'Quality & Safety',
      description: 'We are committed to the highest standards of quality and safety in all our projects',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Customer Satisfaction',
      description: 'We put customer satisfaction at the forefront of our priorities and strive to exceed their expectations',
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Innovation & Development',
      description: 'We keep up with the latest technologies and methods in the contracting field',
    },
    {
      icon: 'ri-team-line',
      title: 'Teamwork',
      description: 'We believe in the power of teamwork and collaboration to achieve the best results',
    },
  ];

  const advantages = [
    {
      icon: 'ri-trophy-line',
      title: 'Industry Leadership',
      description: 'Leading provider of crane rental and heavy equipment services in Saudi Arabia',
      color: 'from-accent to-accent-dark',
    },
    {
      icon: 'ri-time-line',
      title: '24/7 Support',
      description: 'Round-the-clock technical support and emergency response team',
      color: 'from-primary to-primary-dark',
    },
    {
      icon: 'ri-tools-line',
      title: 'Modern Fleet',
      description: 'State-of-the-art equipment from SANY, XCMG, and Tadano manufacturers',
      color: 'from-accent to-accent-dark',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Certified Operations',
      description: 'Fully licensed and certified according to Saudi Arabian standards',
      color: 'from-primary to-primary-dark',
    },
    {
      icon: 'ri-user-star-line',
      title: 'Expert Team',
      description: 'Highly trained and experienced operators and technicians',
      color: 'from-accent to-accent-dark',
    },
    {
      icon: 'ri-price-tag-3-line',
      title: 'Competitive Pricing',
      description: 'Best value for money with flexible rental packages',
      color: 'from-primary to-primary-dark',
    },
  ];

  const goals = [
    {
      icon: 'ri-rocket-line',
      title: 'Expansion',
      description: 'Expand our services to cover all regions of Saudi Arabia',
    },
    {
      icon: 'ri-star-line',
      title: 'Excellence',
      description: 'Maintain the highest standards of quality and safety in all operations',
    },
    {
      icon: 'ri-global-line',
      title: 'Innovation',
      description: 'Adopt the latest technologies and equipment in the industry',
    },
    {
      icon: 'ri-heart-line',
      title: 'Sustainability',
      description: 'Implement environmentally friendly practices in all our projects',
    },
  ];

  const stats = [
    { number: '15+', label: 'Years Experience' },
    { number: '500+', label: 'Projects Completed' },
    { number: '200+', label: 'Heavy Equipment' },
    { number: '100%', label: 'Client Satisfaction' },
  ];

  return (
    <div className="min-h-screen bg-white font-inter">
      <Navbar />
      
      <main className="pt-32">
        {/* Hero Section */}
        <section className="relative py-32 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://public.readdy.ai/ai/img_res/93603ebbfdea3174903844869d714040.jpg"
              alt="About Us"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-primary/80 via-primary/70 to-primary/80"></div>
          </div>

          <div className="container mx-auto px-4 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <motion.h1 
                className="text-5xl md:text-7xl font-poppins font-black text-white mb-6"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                ABOUT US
              </motion.h1>
              <motion.p 
                className="text-xl md:text-2xl text-white/90 font-inter leading-relaxed"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                Leading Saudi company in general contracting and crane rental services
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.5 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="text-center"
                >
                  <motion.div 
                    className="text-5xl md:text-6xl font-poppins font-black text-accent mb-2"
                    whileHover={{ scale: 1.1 }}
                  >
                    {stat.number}
                  </motion.div>
                  <div className="text-neutral-600 font-inter text-sm md:text-base">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* About Content */}
        <section className="py-24 bg-neutral-50" ref={ref1}>
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={isInView1 ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8 }}
              >
                <motion.div 
                  className="flex items-center gap-2 mb-4"
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView1 ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <div className="w-2 h-2 bg-accent rounded-full"></div>
                  <span className="text-accent font-inter font-semibold text-sm uppercase tracking-wider">About Us</span>
                </motion.div>
                
                <motion.h2 
                  className="text-4xl md:text-5xl font-poppins font-black text-primary mb-6 leading-tight"
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView1 ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.3 }}
                >
                  Rawafi Al-Mutahedoon
                  <br />
                  <span className="text-accent">Contracting</span>
                </motion.h2>
                
                <motion.p 
                  className="text-neutral-600 font-inter text-lg leading-relaxed mb-6"
                  initial={{ opacity: 0 }}
                  animate={isInView1 ? { opacity: 1 } : {}}
                  transition={{ duration: 0.8, delay: 0.4 }}
                >
                  Rawafi Al-Mutahedoon Contracting was established with the aim of providing distinguished services in the field of general contracting and crane rental in the Kingdom of Saudi Arabia. We are proud of our experience extending over 15 years in implementing major construction projects.
                </motion.p>
                
                <motion.p 
                  className="text-neutral-600 font-inter text-lg leading-relaxed mb-8"
                  initial={{ opacity: 0 }}
                  animate={isInView1 ? { opacity: 1 } : {}}
                  transition={{ duration: 0.8, delay: 0.5 }}
                >
                  We are committed to the highest standards of quality and professional safety, and we always strive to achieve customer satisfaction by providing innovative and effective solutions that meet their needs and exceed their expectations.
                </motion.p>

                <div className="space-y-4">
                  {[
                    'Saudi expertise extending over 15 years in contracting',
                    'Strict commitment to professional safety and quality standards',
                    'Qualified team trained to the highest level'
                  ].map((text, index) => (
                    <motion.div 
                      key={index}
                      className="flex items-start gap-3"
                      initial={{ opacity: 0, x: -20 }}
                      animate={isInView1 ? { opacity: 1, x: 0 } : {}}
                      transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                    >
                      <div className="w-6 h-6 bg-accent rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <p className="text-neutral-700 font-inter text-base">
                        {text}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={isInView1 ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8 }}
                className="relative"
              >
                <motion.div 
                  className="relative rounded-3xl overflow-hidden shadow-2xl h-[600px]"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <img
                    src="https://readdy.ai/api/search-image?query=professional%20construction%20site%20with%20large%20mobile%20crane%20lifting%20heavy%20materials%20modern%20construction%20project%20in%20Saudi%20Arabia%20with%20tower%20cranes%20and%20heavy%20equipment%20industrial%20construction%20scene%20with%20multiple%20cranes%20working%20on%20building%20project%20professional%20construction%20photography%20with%20blue%20sky%20and%20construction%20workers%20in%20safety%20gear&width=800&height=1000&seq=about-ceo-section-image-001&orientation=portrait"
                    alt="Construction Site"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/40 to-transparent"></div>
                  
                  <motion.div 
                    className="absolute bottom-0 left-0 right-0 p-10"
                    initial={{ opacity: 0, y: 30 }}
                    animate={isInView1 ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8, delay: 0.4 }}
                  >
                    <div className="flex items-center gap-4 mb-6">
                      <img
                        src="https://readdy.ai/api/search-image?query=professional%20Saudi%20Arabian%20male%20business%20executive%20in%20traditional%20thobe%20and%20ghutra%20confident%20CEO%20portrait%20professional%20headshot%20photography%20construction%20company%20director%20middle%20eastern%20businessman%20with%20modern%20office%20background&width=200&height=200&seq=ceo-avatar-001&orientation=squarish"
                        alt="CEO"
                        className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg"
                      />
                      <div>
                        <div className="text-white font-poppins font-bold text-xl">Eng. Mohammed Al-Otaibi</div>
                        <div className="text-white/80 font-inter text-sm">Chief Executive Officer</div>
                      </div>
                    </div>

                    <blockquote className="text-2xl md:text-3xl font-poppins font-bold text-white leading-relaxed mb-4">
                      "We are proud to be the first choice for major projects in the Kingdom of Saudi Arabia"
                    </blockquote>

                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        <motion.div 
                          className="w-2 h-2 bg-white/60 rounded-full"
                          animate={{ scale: [1, 1.3, 1] }}
                          transition={{ duration: 2, repeat: Infinity, delay: 0 }}
                        ></motion.div>
                        <motion.div 
                          className="w-2 h-2 bg-white/60 rounded-full"
                          animate={{ scale: [1, 1.3, 1] }}
                          transition={{ duration: 2, repeat: Infinity, delay: 0.3 }}
                        ></motion.div>
                        <motion.div 
                          className="w-2 h-2 bg-white rounded-full"
                          animate={{ scale: [1, 1.3, 1] }}
                          transition={{ duration: 2, repeat: Infinity, delay: 0.6 }}
                        ></motion.div>
                      </div>
                      <span className="text-white/60 font-inter text-xs ml-2">Leading Company</span>
                    </div>
                  </motion.div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Vision & Mission */}
        <section className="py-24 bg-white" ref={ref2}>
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={isInView2 ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6 }}
                whileHover={{ y: -10 }}
                className="bg-neutral-50 rounded-3xl p-10 shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <motion.div 
                  className="w-16 h-16 bg-accent/10 rounded-2xl flex items-center justify-center mb-6"
                  whileHover={{ rotate: 360, scale: 1.1 }}
                  transition={{ duration: 0.6 }}
                >
                  <i className="ri-eye-line text-4xl text-accent"></i>
                </motion.div>
                <h3 className="text-3xl font-poppins font-black text-primary mb-4">Our Vision</h3>
                <p className="text-neutral-600 font-inter text-lg leading-relaxed">
                  To be the leading company in the field of contracting and crane rental in the Kingdom of Saudi Arabia, and a model to be emulated in quality, safety and innovation.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={isInView2 ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.2 }}
                whileHover={{ y: -10 }}
                className="bg-neutral-50 rounded-3xl p-10 shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <motion.div 
                  className="w-16 h-16 bg-accent/10 rounded-2xl flex items-center justify-center mb-6"
                  whileHover={{ rotate: 360, scale: 1.1 }}
                  transition={{ duration: 0.6 }}
                >
                  <i className="ri-compass-3-line text-4xl text-accent"></i>
                </motion.div>
                <h3 className="text-3xl font-poppins font-black text-primary mb-4">Our Mission</h3>
                <p className="text-neutral-600 font-inter text-lg leading-relaxed">
                  Providing distinguished services in the field of contracting and crane rental with the highest standards of quality and safety, while adhering to deadlines and achieving customer satisfaction.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Goals */}
        <section className="py-24 bg-neutral-50" ref={ref3}>
          <div className="container mx-auto px-4 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView3 ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <div className="flex items-center justify-center gap-2 mb-4">
                <div className="w-2 h-2 bg-accent rounded-full"></div>
                <span className="text-accent font-inter font-semibold text-sm uppercase tracking-wider">Our Goals</span>
              </div>
              <h2 className="text-5xl md:text-6xl font-poppins font-black text-primary mb-6">
                Strategic Goals
              </h2>
              <p className="text-neutral-500 font-inter text-xl max-w-2xl mx-auto">
                Our objectives that we strive to achieve in the coming years
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {goals.map((goal, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  animate={isInView3 ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -10, scale: 1.05 }}
                  className="bg-white rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 group border border-neutral-100"
                >
                  <motion.div 
                    className="w-16 h-16 bg-gradient-to-br from-accent to-accent-dark rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-300"
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                  >
                    <i className={`${goal.icon} text-3xl text-white`}></i>
                  </motion.div>
                  <h3 className="text-xl font-poppins font-bold text-primary mb-3">
                    {goal.title}
                  </h3>
                  <p className="text-neutral-600 font-inter text-sm leading-relaxed">
                    {goal.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Advantages */}
        <section className="py-24 bg-white" ref={ref4}>
          <div className="container mx-auto px-4 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView4 ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <div className="flex items-center justify-center gap-2 mb-4">
                <div className="w-2 h-2 bg-accent rounded-full"></div>
                <span className="text-accent font-inter font-semibold text-sm uppercase tracking-wider">Why Choose Us</span>
              </div>
              <h2 className="text-5xl md:text-6xl font-poppins font-black text-primary mb-6">
                Our Advantages
              </h2>
              <p className="text-neutral-500 font-inter text-xl max-w-2xl mx-auto">
                What makes us the preferred choice for major projects
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {advantages.map((advantage, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={isInView4 ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -15, scale: 1.05 }}
                  className="relative bg-neutral-50 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 group overflow-hidden"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${advantage.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                  
                  <motion.div 
                    className={`w-20 h-20 bg-gradient-to-br ${advantage.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-300 relative z-10`}
                    whileHover={{ rotate: [0, -10, 10, -10, 0] }}
                    transition={{ duration: 0.5 }}
                  >
                    <i className={`${advantage.icon} text-4xl text-white`}></i>
                  </motion.div>
                  
                  <h3 className="text-2xl font-poppins font-bold text-primary mb-4 relative z-10">
                    {advantage.title}
                  </h3>
                  <p className="text-neutral-600 font-inter text-base leading-relaxed relative z-10">
                    {advantage.description}
                  </p>

                  <motion.div 
                    className="absolute bottom-4 right-4 w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    whileHover={{ scale: 1.2, rotate: 45 }}
                  >
                    <i className="ri-arrow-right-up-line text-xl text-accent"></i>
                  </motion.div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-24 bg-neutral-50" ref={ref5}>
          <div className="container mx-auto px-4 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView5 ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <div className="flex items-center justify-center gap-2 mb-4">
                <div className="w-2 h-2 bg-accent rounded-full"></div>
                <span className="text-accent font-inter font-semibold text-sm uppercase tracking-wider">Our Values</span>
              </div>
              <h2 className="text-5xl md:text-6xl font-poppins font-black text-primary mb-6">
                Company Values
              </h2>
              <p className="text-neutral-500 font-inter text-xl max-w-2xl mx-auto">
                The principles we believe in and adhere to in all our work
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50, rotateY: -90 }}
                  animate={isInView5 ? { opacity: 1, y: 0, rotateY: 0 } : {}}
                  transition={{ duration: 0.8, delay: index * 0.15 }}
                  whileHover={{ y: -10, rotateY: 5 }}
                  className="bg-white rounded-2xl p-8 hover:bg-accent/5 hover:shadow-lg transition-all duration-300 group"
                >
                  <motion.div 
                    className="w-16 h-16 bg-accent/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-accent group-hover:scale-110 transition-all duration-300"
                    whileHover={{ rotate: [0, 360] }}
                    transition={{ duration: 0.6 }}
                  >
                    <i className={`${value.icon} text-3xl text-accent group-hover:text-white`}></i>
                  </motion.div>
                  <h3 className="text-xl font-poppins font-bold text-primary mb-3">
                    {value.title}
                  </h3>
                  <p className="text-neutral-600 font-inter text-sm leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
